import {useEffect, useState, type FocusEvent, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {
  useDecidirContradiccion,
  type DecisionContradiccion,
  type PreguntaCargo,
} from '@/hooks/empresa/perfil_cargo/use-preguntas-cargo'
import {translateApiError} from '@/lib/errors'
import {tope} from '@/lib/campos-texto'

const MANDA: DecisionContradiccion[] = ['respuesta', 'matriz']

/** Texto libre y obligatorio: sin motivo, la decisión no se puede auditar. */
function validateMotivoDecision(motivo: string): string | null {
  return motivo.trim() ? null : 'errors.preguntaMotivoVacio'
}

/** E10-H04. Ninguna versión viene preseleccionada: Kairos no elige cuál manda. */
export function PreguntaContradiccionModal({
  open,
  onOpenChange,
  businessId,
  positionId,
  pregunta,
  puedeFirmar,
  razonBloqueo,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  positionId: string
  pregunta: PreguntaCargo | null
  puedeFirmar: boolean
  razonBloqueo: string | null
}) {
  const {t} = useTranslation()
  const decidir = useDecidirContradiccion(businessId, positionId)
  const [manda, setManda] = useState<DecisionContradiccion | null>(null)
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | undefined>()
  const reset = decidir.reset

  useEffect(() => {
    if (open && pregunta) {
      setManda(pregunta.decision_contradiccion ?? null)
      setMotivo(pregunta.decision_contradiccion_motivo ?? '')
      setError(undefined)
      reset()
    }
  }, [open, pregunta, reset])

  if (!pregunta) return null
  const abierta = pregunta

  function blurMotivo(e: FocusEvent<HTMLTextAreaElement>) {
    setError(validateMotivoDecision(e.target.value) ?? undefined)
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const hallazgo = validateMotivoDecision(motivo)
    setError(hallazgo ?? undefined)
    if (hallazgo || !manda) return
    decidir.mutate(
      {
        preguntaKey: abierta.pregunta_key,
        manda,
        motivo: motivo.trim(),
        decisionVista: abierta.decision_contradiccion,
      },
      {onSuccess: () => onOpenChange(false)}
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('preguntaCargo.contradiccion.titulo')}</DialogTitle>
          <DialogDescription>{t('preguntaCargo.contradiccion.explicacion')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-2 text-sm" data-testid="contradiccion-versiones">
            <p className="surface rounded-lg px-3 py-2">
              {t('preguntaCargo.contradiccion.loQueContesto', {
                enunciado: abierta.enunciado,
                respuesta: abierta.respuesta,
              })}
            </p>
            <p className="surface rounded-lg px-3 py-2">
              {t('preguntaCargo.contradiccion.loQueDiceLaMatriz', {
                fila: abierta.contradice_fila_numero,
                descripcion: abierta.contradice_fila_descripcion,
              })}
            </p>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('preguntaCargo.contradiccion.cualManda')}</legend>
            {MANDA.map((opcion) => (
              <label key={opcion} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="contradiccion-manda"
                  value={opcion}
                  data-testid={`contradiccion-manda-${opcion}`}
                  checked={manda === opcion}
                  onChange={() => setManda(opcion)}
                  className="mt-0.5 accent-primary"
                />
                <span>{t(`preguntaCargo.contradiccion.manda.${opcion}`)}</span>
              </label>
            ))}
          </fieldset>

          <div className="space-y-2">
            <Label htmlFor="contradiccion-motivo">{t('preguntaCargo.contradiccion.motivo')}</Label>
            <Textarea
              maxLength={tope('NOTA')}
              id="contradiccion-motivo"
              data-testid="contradiccion-motivo-input"
              rows={3}
              value={motivo}
              onChange={(e) => {
                setMotivo(e.target.value)
                setError(undefined)
              }}
              onBlur={blurMotivo}
              aria-invalid={!!error}
            />
            <FieldError error={error} />
          </div>

          {!puedeFirmar && razonBloqueo && (
            <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="contradiccion-bloqueo">
              {razonBloqueo}
            </p>
          )}
          <FormDialogFooter
            error={decidir.isError ? translateApiError(decidir.error) : null}
            pending={decidir.isPending}
            submitDisabled={!manda || !puedeFirmar}
            submitLabel={t('preguntaCargo.contradiccion.guardar')}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
